import React, { useState, useEffect } from 'react';
import {
  Container, Paper, Typography, TextField, Button, Box, Alert,
  Tabs, Tab, Stack, IconButton, InputAdornment, Divider
} from '@mui/material';
import {
  Login as LoginIcon, PersonAdd,
  Analytics, Visibility, VisibilityOff
} from '@mui/icons-material';
import { useAuth } from './AuthContext';

function Login({ onTabChange }) {
  const [activeTab, setActiveTab] = useState(0);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: ''
  });

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const { 
    login,
    register,
    signInWithGoogle,
    pendingReferralCode,
    isAuthenticated 
  } = useAuth();





  // Auto-redirect after successful authentication
  useEffect(() => {
    // Redirect if user is authenticated and there's a success message
    if (isAuthenticated && success) {
      const timer = setTimeout(() => {
        if (onTabChange && isAuthenticated) {
          // Check if there's a return tab stored (e.g., from templates page)
          const returnToTab = localStorage.getItem('returnToTab');
          if (returnToTab) {
            const tabNumber = parseInt(returnToTab);
            onTabChange(tabNumber); // Redirect to the stored tab (e.g., templates page)
            localStorage.removeItem('returnToTab'); // Clear the stored tab
          } else {
            onTabChange(1); // Default: Switch to Dashboard tab
          }
        }
      }, 2000); // 2 second delay to show success message
      return () => clearTimeout(timer);
    }
  }, [isAuthenticated, success, onTabChange]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setError('');
    setSuccess('');
  };

  const handleInputChange = (field) => (event) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
    setError('');
    setSuccess('');
  };

  // Traditional login function
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const result = await login(formData.email, formData.password);
      if (result.success) {
        setSuccess('Login successful! Redirecting to dashboard...');
        // Clear form data after successful login
        setFormData({
          username: '',
          email: '',
          password: '',
          confirmPassword: ''
        });
      } else {
        setError(result.error);
      }
    } catch (error) {
      setError('Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Traditional registration function
  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    try {
      const result = await register(formData.email, formData.password, formData.username);
      
      if (result.success) {
        setSuccess(result.message);
        // Clear form data after successful registration
        setFormData({
          username: '',
          email: '',
          password: '',
          confirmPassword: ''
        });
      } else {
        setError(result.error);
      }
    } catch (error) {
      setError('Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Google sign-in handler
  const handleGoogleSignIn = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const result = await signInWithGoogle();
      if (result.success) {
        setSuccess('Redirecting to Google...');
      } else {
        setError(result.error);
        setLoading(false);
      }
    } catch (error) {
      setError('Failed to sign in with Google. Please try again.');
      setLoading(false);
    }
  };



  return (
    <Container maxWidth="sm" sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Box textAlign="center" sx={{ mb: 4 }}>
          <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
            <Analytics color="primary" />
            Login or Register
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Access your transactions dashboard
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            {success}
          </Alert>
        )}

        {pendingReferralCode && (
          <Alert severity="info" sx={{ mb: 3 }}>
            Referral code <strong>{pendingReferralCode}</strong> is saved for this signup. It will be applied after your account is activated and you sign in.
          </Alert>
        )}

        <Tabs value={activeTab} onChange={handleTabChange} centered sx={{ mb: 3 }}>
          <Tab label="Login" icon={<LoginIcon />} iconPosition="start" />
          <Tab label="Register" icon={<PersonAdd />} iconPosition="start" />
        </Tabs>

        {activeTab === 0 && (
          <Box component="form" onSubmit={handleLogin}>
            <Stack spacing={3}>
              <Button
                variant="outlined"
                fullWidth
                size="large"
                onClick={handleGoogleSignIn}
                disabled={loading}
                sx={{
                  borderColor: '#4285F4',
                  color: '#4285F4',
                  '&:hover': {
                    borderColor: '#357AE8',
                    backgroundColor: 'rgba(66, 133, 244, 0.04)'
                  }
                }}
              >
                <Box
                  component="img"
                  src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
                  alt="Google"
                  sx={{ width: 20, height: 20, mr: 1 }}
                  onError={(e) => {
                    // Fallback if image fails to load
                    e.target.style.display = 'none';
                  }}
                />
                Login with Google
              </Button>

              <Divider>OR</Divider>

              <TextField
                label="Email"
                type="email"
                value={formData.email}
                onChange={handleInputChange('email')}
                fullWidth
                required
              />
              
              <TextField
                label="Password"
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={handleInputChange('password')}
                fullWidth
                required
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Button
                type="submit"
                variant="contained"
                fullWidth
                size="large"
                disabled={loading || !formData.email || !formData.password}
                startIcon={<LoginIcon />}
              >
                {loading ? 'Logging in...' : 'Login'}
              </Button>
            </Stack>
          </Box>
        )}

        {activeTab === 1 && (
          <Box component="form" onSubmit={handleRegister}>
            <Stack spacing={3}>
              <Button
                variant="outlined"
                fullWidth
                size="large"
                onClick={handleGoogleSignIn}
                disabled={loading}
                sx={{
                  borderColor: '#4285F4',
                  color: '#4285F4',
                  '&:hover': {
                    borderColor: '#357AE8',
                    backgroundColor: 'rgba(66, 133, 244, 0.04)'
                  }
                }}
              >
                <Box
                  component="img"
                  src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
                  alt="Google"
                  sx={{ width: 20, height: 20, mr: 1 }}
                  onError={(e) => {
                    // Fallback if image fails to load
                    e.target.style.display = 'none';
                  }}
                />
                Sign up with Google
              </Button>

              <Divider>OR</Divider>

              <TextField
                label="Username"
                value={formData.username}
                onChange={handleInputChange('username')}
                fullWidth
                required
              />
              
              <TextField
                label="Email"
                type="email"
                value={formData.email}
                onChange={handleInputChange('email')}
                fullWidth
                required
              />
              
              <TextField
                label="Password"
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={handleInputChange('password')}
                fullWidth
                required
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              
              <TextField
                label="Confirm Password"
                type={showPassword ? 'text' : 'password'}
                value={formData.confirmPassword}
                onChange={handleInputChange('confirmPassword')}
                fullWidth
                required
                error={formData.password !== formData.confirmPassword && formData.confirmPassword !== ''}
                helperText={formData.password !== formData.confirmPassword && formData.confirmPassword !== '' ? 'Passwords do not match' : ''}
              />

              <Button
                type="submit"
                variant="contained"
                fullWidth
                size="large"
                disabled={loading || !formData.username || !formData.email || !formData.password || !formData.confirmPassword || formData.password !== formData.confirmPassword}
                startIcon={<PersonAdd />}
              >
                {loading ? 'Creating Account...' : 'Create Account'}
              </Button>
            </Stack>
          </Box>
        )}



        <Typography variant="body2" color="text.secondary" sx={{ mt: 3, textAlign: 'center' }}>
          <Box
            component="a"
            href="/delete-account"
            sx={{ color: '#388e3c', fontWeight: 600, textDecoration: 'underline' }}
          >
            Delete your account and data
          </Box>
        </Typography>
      </Paper>
    </Container>
  );
}

export default Login; 